import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { CSVResponse } from './premium-acreage-user.service';

import { environment } from '@env/environment';
import { EnrollmentGroup, PaidPaymentExport, Parcel, PaymentPeriod, PaymentPeriodDetailDashboard, PaymentPeriodSummary, Program, Site, SiteDashboard, SiteReviewCheck, SiteReviewNote, Stand, } from '@shared/models';
import { deserialize, serialize } from '@shared/models/base-helper';
import { SiteDocusignEnvelope } from '@shared/models/site-docusign-envelope.model';
import { SiteStatus } from '@shared/models/site-status';
import { SpinnerService } from '@shared/spinner/spinner.service';
import * as fromApp from '@store';
import { Base } from '@shared/models/base.model';
import { ToastService } from '@shared/services/toast.service';
import { ToastLevel } from '@shared/models/toast-message.module';

interface QueryResponse {
  results: any[];
  total: number;
}

interface DeserializedResponse {
  results: Site[];
  total: number;
}

interface DeserializedDashboardResponse {
  results: SiteDashboard[];
  total: number;
}

interface PaymentPeriodQueryResponse {
  results: any[];
  total: number;
}

interface DeserializedPaymentPeriodResponse {
  results: PaymentPeriodDetailDashboard[];
  total: number;
}

interface FileDownloadResponse {
  bytes: string;
}

@Injectable({
  providedIn: 'root',
})
export class SiteService {
  apiUrl = environment.apiUrl + '/sites/';

  destroyRef = inject(DestroyRef);

  constructor(
    public dialog: MatDialog,
    readonly http: HttpClient,
    readonly spinnerService: SpinnerService,
    readonly toastService: ToastService,
    readonly store: Store<fromApp.AppState>
  ) {}

  updateSiteStep(id: number, step: string) {
    const body = { site_id: id, step: step };
    this.http.post<boolean>(`${this.apiUrl}updateStep`, body).subscribe({
      error: (err) => this.toastService.sendMessage("Error updating Site Step", ToastLevel.WARN)
    });
  }

  setAssignedAdmin(siteId: number, adminId: number): Observable<Site> {
    return this.http
      .patch<any>(`${this.apiUrl}${siteId}/assigned_admin`, {
        admin_id: adminId,
      })
      .pipe(map(serializedSite => deserialize(serializedSite, Site)));
  }

  getSite(id: number): Observable<Site> {
    return this.http.get<any>(`${this.apiUrl}${id}`).pipe(map(serializedSite => deserialize(serializedSite, Site)));
  }

  getAvailableSites(userId?: number): Observable<Site[]> {
    let params = new HttpParams();
    if (userId) {
      params = params.append('user_id', userId.toString());
    }
    return this.http
      .get<any[]>(`${this.apiUrl}my`, { params })
      .pipe(
        map(sites => sites.map((serializedSite: any) => deserialize(serializedSite, Site)).sort((a, b) => a.id - b.id))
      );
  }

  getSitesForStatDashboard(): Observable<Site[]> {
    return this.http.get<any[]>(`${this.apiUrl}stat_dashboard/`).pipe(
      map((response: any) => deserialize(response, Site))
    );
  }

  getSitesForDashboard: (filterString: string) => Observable<DeserializedDashboardResponse> = (filterString) => {
    const filterUrl = `${this.apiUrl}site_dashboard/${filterString}`;

    return this.http.get<QueryResponse>(filterUrl).pipe(
      map(response => {
        const deserializedResponse: DeserializedDashboardResponse = {
          results: [],
          total: response.total,
        };
        for (const serializedSite of response.results) {
          deserializedResponse.results.push(deserialize(serializedSite, SiteDashboard));
        }
        return deserializedResponse;
      })
    );
  }

  getFilteredSites: (filterString: string) => Observable<DeserializedResponse> = (filterString) => {
    const filterUrl = this.apiUrl + filterString;

    return this.http.get<QueryResponse>(filterUrl).pipe(
      map(response => {
        const deserializedResponse: DeserializedResponse = {
          results: [],
          total: response.total,
        };
        for (const serializedSite of response.results) {
          deserializedResponse.results.push(deserialize(serializedSite, Site));
        }
        return deserializedResponse;
      })
    );
  }

  changeSitesState(
    siteIds: Array<number>,
    transitionToState: string,
    clearSubmitData: boolean = false,
    statusNote: string = null
  ): Observable<any> {
    const payload = {
      site_ids: siteIds,
      transition_to_state: transitionToState,
      clear_original_submitted_date: clearSubmitData,
      status_note: statusNote,
    };

    return this.http.post<any>(`${this.apiUrl}bulk_edit`, payload).pipe(
      map((response: any) => deserialize(response, Base))
    );
  }

  resetAvailableEnrollmentGroups(id: number): Observable<Site> {
    this.spinnerService.show('app-spinner', 'Updating Site');
    return this.http.patch<any>(`${this.apiUrl}${id}/reset_available_enrollment_groups`, null).pipe(
      map((serializedSite: any) => deserialize(serializedSite, Site)),
      finalize(() => this.spinnerService.hide('app-spinner'))
    );
  }

  updateSiteParcels(id: number, parcels: Array<Parcel>): Observable<Site> {
    this.spinnerService.show('app-spinner', 'Updating Parcels');
    return this.http.patch<any>(`${this.apiUrl}${id}/parcels`, parcels).pipe(
      map((serializedSite: any) => deserialize(serializedSite, Site)),
      finalize(() => this.spinnerService.hide('app-spinner'))
    );
  }

  updateSiteStands(id: number, stands: Array<Stand>): Observable<Site> {
    return this.http.patch<any>(`${this.apiUrl}${id}/stands`, stands).pipe(
      map((serializedSite: any) => deserialize(serializedSite, Site)),
    );
  }

  updateSiteMetadata(id: number, site_in: any): Observable<Site> {
    this.spinnerService.show('app-spinner', 'Updating Site');
    return this.http.patch<any>(`${this.apiUrl}${id}/metadata`, site_in).pipe(
      map((serializedSite: any) => deserialize(serializedSite, Site)),
      finalize(() => this.spinnerService.hide('app-spinner'))
    );
  }

  updateInitialAdjustment(id: number, adjustment: number): Observable<Site> {
    this.spinnerService.show('app-spinner', 'Updating Site');
    return this.http
      .patch<any>(`${this.apiUrl}${id}/initial_adjustment`, null, {
        params: new HttpParams().set('adjustment', adjustment),
      })
      .pipe(
        map((serializedSite: any) => deserialize(serializedSite, Site)),
        finalize(() => this.spinnerService.hide('app-spinner'))
      );
  }

  updateEnrollmentGroup(id: number, enrollment_group_id: number, accepted_custom_terms: boolean): Observable<Site> {
    this.spinnerService.show('app-spinner', 'Updating Site');
    return this.http
      .patch<any>(`${this.apiUrl}${id}/enrollment_group`, null, {
        params: new HttpParams().set('enrollment_group_id', enrollment_group_id)
          .set('accepted_custom_terms', accepted_custom_terms),
      })
      .pipe(
        map((serializedSite: any) => deserialize(serializedSite, Site)),
        finalize(() => this.spinnerService.hide('app-spinner'))
      );
  }

  updateSiteEligibilityReport(
    id: number,
    programs: Program[] = [],
    other_programs: string = null,
    report: any = null
  ): Observable<Site> {
    this.spinnerService.show('app-spinner', 'Updating Site');
    return this.http
      .patch<any>(`${this.apiUrl}${id}/eligibility_report_programs`, {
        programs,
        other_programs,
        report,
      })
      .pipe(
        map((serializedSite: any) => deserialize(serializedSite, Site)),
        finalize(() => this.spinnerService.hide('app-spinner'))
      );
  }

  deleteSite(id: number): Observable<Site> {
    return this.http
      .delete<any>(`${this.apiUrl}${id}`)
      .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
  }

  submitSite(id: number, enrollmentGroupId: number, profile: any): Observable<Site> {
    return this.http
      .put<any>(`${this.apiUrl}${id}/submit`, {
        enrollment_group_id: enrollmentGroupId,
        profile_update: profile,
      })
      .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
  }

  siteNeedsRestart(id: number): Observable<boolean> {
    return this.http.get<any>(`${this.apiUrl}${id}/needs_restart`).pipe(map(resp => resp.needs_restart));
  }

  sitePublicOverlap(id: number): Observable<number> {
    return this.http.get<any>(`${this.apiUrl}${id}/get_public_overlap`);
  }

  sendContract(siteId: string): Observable<Site> {
    return this.http
      .put<any>(`${this.apiUrl}send_contract?site_identifier=${siteId}`, {})
      .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
  }

  downloadChecklist(siteId: number): Observable<string> {
    return this.http
      .get(`${this.apiUrl}${siteId}/download_checklist`)
      .pipe(map((response: FileDownloadResponse) => response.bytes));
  }

  downloadContract(siteId: string): Observable<string> {
    return this.http
      .put(`${this.apiUrl}download_contract?site_identifier=${siteId}`, {})
      .pipe(map((response: FileDownloadResponse) => response.bytes));
  }

  updateSiteApplicant(siteId: number, userId: number): Observable<Site> {
    return this.http
      .patch<any>(`${this.apiUrl}${siteId}/applicant`, {
        user_id: userId,
      })
      .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
  }

  createSite(enrollmentGroupId: number): Observable<Site> {
    if (enrollmentGroupId) {
      this.spinnerService.show('app-spinner');
      return this.http
        .post<any>(`${this.apiUrl}`, {
          enrollment_group_id: enrollmentGroupId,
          name: `New Site - ${new Date().toLocaleString()}`,
        })
        .pipe(
          map((serializedSite: any) => deserialize(serializedSite, Site)),
          finalize(() => this.spinnerService.hide('app-spinner'))
        );
    } else {
      this.spinnerService.hide('app-spinner');
      this.toastService.sendMessage("Region not found. Please try again later.", ToastLevel.WARN);
      return new Observable<Site>(subscriber => subscriber.complete());
    }
  }

  getPaidPaymentPeriodSummary() {
    return this.http.get<Array<PaidPaymentExport>>(this.apiUrl + 'paymentPeriods/paid_details/').pipe(
      map(response =>
        response.map(periodDetail => deserialize(periodDetail, PaidPaymentExport))
      )
    );
  }

  getFilteredPaymentPeriods(
    filterString: string,
    type: string
  ): Observable<DeserializedPaymentPeriodResponse> {
    const filterUrl = this.apiUrl + `paymentPeriods/${type}/` + filterString;
    return this.http.get<PaymentPeriodQueryResponse>(filterUrl).pipe(
      map(response => {
        const deserializedResponse: DeserializedPaymentPeriodResponse = {
          results: response.results.map(serializedPaymentPeriodSummary =>
            deserialize(serializedPaymentPeriodSummary, PaymentPeriodDetailDashboard)
          ),
          total: response.total,
        };
        return deserializedResponse;
      })
    );
  }

  getDetailedPaymentPeriod(
    periods: Array<PaymentPeriodSummary>,
    includeAllSubmitted: boolean = false
  ): Observable<PaymentPeriodDetailDashboard[]> {
    let serializedPeriods = periods.map(period => serialize(period));
    return this.http
      .post<any[]>(this.apiUrl + `paymentPeriods/detail/?include_all=${includeAllSubmitted}`, serializedPeriods)
      .pipe(
        map(response =>
          response.map(serializedPaymentPeriodDetail => deserialize(serializedPaymentPeriodDetail, PaymentPeriodDetailDashboard))
        )
      );
  }

  saveSiteReviewNote(siteReviewNote: SiteReviewNote): Observable<Site> {
    if (siteReviewNote.id) {
      return this.http
        .put<any>(`${this.apiUrl}site_review_note/${siteReviewNote.id}`, serialize(siteReviewNote))
        .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
    } else {
      return this.http
        .post<any>(`${this.apiUrl}site_review_note`, serialize(siteReviewNote))
        .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
    }
  }

  saveSiteReviewCheck(siteReviewCheck: SiteReviewCheck): Observable<Site> {
    if (siteReviewCheck.id) {
      return this.http
        .put<any>(`${this.apiUrl}site_review_check/${siteReviewCheck.id}`, serialize(siteReviewCheck))
        .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
    } else {
      return this.http
        .post<any>(`${this.apiUrl}site_review_check`, serialize(siteReviewCheck))
        .pipe(map((serializedSite: any) => deserialize(serializedSite, Site)));
    }
  }

  splitSite(siteId: number, effectiveDate: Date, newKml: any, createLeftover: boolean) {
    return this.http
      .post<any>(`${this.apiUrl}${siteId}/split_site`, {
        effective_date: effectiveDate,
        kmls: newKml,
        create_leftover: createLeftover
      })
      .pipe(
        map((response: any) => {
          return response.results;
        })
      );
  }

  updateParcelData(siteId, id, owner, address, parcelNumber, county, state) {
    return this.http
      .patch<any>(`${this.apiUrl}${siteId}/parcels/`, {
        id: id,
        owner: owner,
        parcelNumber: parcelNumber,
        address: address,
        county: county,
        state: state,
      })
      .pipe(
        map((response: any) => {
          return response.results;
        })
      );
  }

  updatePaymentPeriod(siteId: number, updatePaymentPeriod: any): Observable<PaymentPeriod> {
    return this.http
      .patch<any>(
        `${this.apiUrl}${siteId}/paymentPeriods/${updatePaymentPeriod.payment_period.id}/`,
        updatePaymentPeriod
      )
      .pipe(
        map((serializedPaymentPeriod: any) => {
          return deserialize(serializedPaymentPeriod, PaymentPeriod);
        })
      );
  }

  getOwnershipChangeCSV() {
    return this.http
      .get<CSVResponse>(`${this.apiUrl}ownership_change/csv`)
      .pipe(map((response: CSVResponse) => response.csv_string));
  }

  getChangeDetectionCSV(threshold: any) {
    return this.http
      .get<CSVResponse>(`${this.apiUrl}change_detection/csv?threshold=${threshold}`)
      .pipe(map((response: CSVResponse) => response.csv_string));
  }

  getAllSiteStatuses(): Observable<SiteStatus[]> {
    return this.http.get<SiteStatus[]>(`${this.apiUrl}site_statuses/`, { headers: new HttpHeaders().set('X-FC-Noauth', 'true') })
      .pipe(map(siteStatuses => deserialize(siteStatuses, SiteStatus)));
  }

  sendEdoc(id: number, templateId: string) {
    return this.http
      .post(`${this.apiUrl}${id}/send_edoc/${templateId}/`, {})
      .pipe(map(docEnvelope => deserialize(docEnvelope, SiteDocusignEnvelope)));
  }

  getStrataVersion(siteId: number, versionId: string) {
    return this.http.get<any>(`${this.apiUrl}${siteId}/get_strata_version/${versionId}`).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  downloadSitesStrata(sites: Array<number>) {
    return this.http.post<any>(`${this.apiUrl}get_strata_geom/`, { sites: sites }).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  downloadSitesStrataShapefile(sites: Array<number>) {
    return this.http.post(`${this.apiUrl}get_strata_shapefile/`, { sites: sites }, { responseType: 'blob' });
  }

  downloadSitesEnrollment(sites: Array<number>) {
    return this.http.post<any>(`${this.apiUrl}get_enrollment_geom/`, { sites: sites }).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  processRemoval(id: number, geometry: any, clawbackPeriods: number) {
    const body = {
      geometry: JSON.stringify(geometry),
      clawback_periods: clawbackPeriods
    };
    return this.http
      .post<Site>(`${this.apiUrl}${id}/process_removal`, body).pipe(
        map((response: any) => {
          return response;
        })
      );
  }

  siteHasNewStands(siteId: number, stands: any) {
    return this.http.post<any>(`${this.apiUrl}${siteId}/has_new_stands`, stands)
      .pipe(map(response => deserialize(response, Base)));
  }

  refreshRegridInformation(siteId: number, parcelId: number) {
    return this.http.get<any>(`${this.apiUrl}${siteId}/parcels/${parcelId}/refresh_regrid`)
      .pipe(map(response => deserialize(response, Parcel)));
  }

  getAvailableEnrollmentGroups(siteId: number): Observable<EnrollmentGroup[]> {
    return this.http.get<EnrollmentGroup[]>(`${this.apiUrl}${siteId}/available_enrollment_groups`)
      .pipe(map(response => deserialize(response, EnrollmentGroup)));

  }

  createQuickSite(parcelString: string) {
    return this.http.post<Site>(`${this.apiUrl}quick_site/?parcel_ids=${parcelString}`, {})
      .pipe(map(response => deserialize(response, Site)));
  }
}
